import { SELLER_API_BASE, X_AUTH_API_KEY } from '../../config/api';
import { getGlobal } from '../../global';
import { getSellerAccessToken } from '../../util/sellerSession';
import { makeAuthenticatedRequest } from '../../util/simpleAuth';

export type SellerStatus = 'PENDING' | 'ACTIVE' | 'SUSPENDED' | 'DELETED';

export interface SellerConfig {
  orders: { add: boolean; change: boolean; cancel: boolean };
  customers: { upsert: boolean };
  inventory: { read: boolean };
}

export interface SellerMe {
  id: string;
  name: string;
  status: SellerStatus;
  telegramUsername: string;
  telegramPhone: string | null;
  telegramUserId?: string | null;
  maxQuantityPerProduct: number;
  config: SellerConfig;
}

export interface SellerLoginResponse {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresIn: number;
  scopes: string[];
  seller: SellerMe;
}

export interface SellerRegisterResponse {
  sellerId: string;
  status: string;
  message: string;
}

export type SellerAuthStatus = 'PENDING' | 'ACTIVE' | 'BLOCKED';

export interface SellerAuthStatusResponse {
  status: SellerAuthStatus;
  accessToken?: string | null;
  refreshToken?: string | null;
  expiresIn?: number | null;
  scopes?: string[] | null;
  seller?: SellerMe | null;
}

export class SellerApiError extends Error {
  code: string;

  constructor(code: string, message: string) {
    super(message);
    this.code = code;
  }
}

const SELLER_ERROR_RU: Record<string, string> = {
  SELLER_PENDING_APPROVAL: 'Аккаунт ждёт одобрения администратора',
  SELLER_BLOCKED: 'Аккаунт заблокирован',
  SELLER_PHONE_REQUIRED: 'Сначала завершите вход в Telegram, затем повторите',
  SELLER_PHONE_LOCKED: 'Telegram уже привязан к другому аккаунту',
  SELLER_CONFIG_DENIED: 'Это действие отключено для вашего аккаунта',
  SELL_PRICE_TOO_LOW: 'Цена ниже минимальной',
  MARKET_CLOSED: 'Рынок закрыт',
  USERNAME_TAKEN: 'Такой логин уже занят',
  TELEGRAM_USERNAME_TAKEN: 'Этот Telegram уже зарегистрирован',
  VALIDATION_ERROR: 'Проверьте введённые данные',
  BadCredentialsException: 'Неверный логин или пароль',
  ERROR: 'Что-то пошло не так',
};

function formatMaxQuantityMessage(max: number): string {
  return `Допустимое количество товара — ${max}. Чтобы увеличить лимит, свяжитесь с нами.`;
}

function extractMaxQuantity(message: string): number | null {
  const match = message.match(/max of (\d+)/i)
    || message.match(/seller max(?:imum)?[:\s]+(\d+)/i)
    || message.match(/(\d+)\s*per product/i);
  if (!match) return null;
  const value = Number(match[1]);
  return Number.isFinite(value) && value > 0 ? value : null;
}

function localizeSellerError(code: string, message: string): string {
  if (code === 'SELLER_MAX_QUANTITY') {
    const max = extractMaxQuantity(message) ?? 5;
    return formatMaxQuantityMessage(max);
  }

  const byCode = SELLER_ERROR_RU[code];
  if (byCode) return byCode;

  const normalized = message.trim().toLowerCase();
  if (normalized.includes('exceeds seller max') || normalized.includes('seller max of')) {
    const max = extractMaxQuantity(message) ?? 5;
    return formatMaxQuantityMessage(max);
  }
  if (normalized.includes('invalid username or password')) {
    return 'Неверный логин или пароль';
  }
  if (normalized.includes('waiting for approval')) {
    return SELLER_ERROR_RU.SELLER_PENDING_APPROVAL;
  }
  if (normalized.includes('blocked') || normalized.includes('not active')) {
    return SELLER_ERROR_RU.SELLER_BLOCKED;
  }
  if (normalized.includes('username already')) {
    return SELLER_ERROR_RU.USERNAME_TAKEN;
  }
  if (normalized.includes('telegram') && normalized.includes('already')) {
    return SELLER_ERROR_RU.TELEGRAM_USERNAME_TAKEN;
  }
  if (normalized.includes('at least 6')) {
    return 'Пароль должен быть не короче 6 символов';
  }
  if (normalized.includes('are required')) {
    return 'Заполните все поля';
  }
  if (normalized.includes('request failed') || !message.trim()) {
    return SELLER_ERROR_RU.ERROR;
  }

  return message;
}

export async function parseSellerApiError(response: Response): Promise<SellerApiError> {
  try {
    const body = await response.json();
    const first = Array.isArray(body?.errors) ? body.errors[0] : null;
    const code = String(first?.code || first?.error || body?.code || body?.error || 'ERROR');
    const raw = String(first?.message || body?.message || response.statusText || 'Request failed');
    return new SellerApiError(code, localizeSellerError(code, raw));
  } catch {
    return new SellerApiError('ERROR', SELLER_ERROR_RU.ERROR);
  }
}

export function getSellerErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof SellerApiError) return error.message;
  if (error instanceof Error) {
    const text = error.message;
    if (text.includes('SELLER_MAX_QUANTITY') || text.includes('exceeds seller max')) {
      const max = extractMaxQuantity(text) ?? 5;
      return formatMaxQuantityMessage(max);
    }
    if (text && !text.startsWith('Failed to') && !text.startsWith('API Error')) {
      return text;
    }
  }
  return fallback;
}

async function parseError(response: Response): Promise<SellerApiError> {
  return parseSellerApiError(response);
}

function publicHeaders(): HeadersInit {
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  if (X_AUTH_API_KEY) {
    headers['x-auth-api-key'] = X_AUTH_API_KEY;
  }
  return headers;
}

export async function registerSeller(request: {
  username: string;
  password: string;
  telegramUsername: string;
}): Promise<SellerRegisterResponse> {
  const response = await fetch(`${SELLER_API_BASE}/auth/register`, {
    method: 'POST',
    headers: publicHeaders(),
    body: JSON.stringify(request),
  });
  if (!response.ok) throw await parseError(response);
  return response.json();
}

export async function loginSeller(request: {
  username: string;
  password: string;
}): Promise<SellerLoginResponse> {
  const response = await fetch(`${SELLER_API_BASE}/auth/login`, {
    method: 'POST',
    headers: publicHeaders(),
    body: JSON.stringify(request),
  });
  if (!response.ok) throw await parseError(response);
  return response.json();
}

export async function checkSellerAuthStatus(request: {
  username: string;
  password: string;
}): Promise<SellerAuthStatusResponse> {
  const response = await fetch(`${SELLER_API_BASE}/auth/status`, {
    method: 'POST',
    headers: publicHeaders(),
    body: JSON.stringify(request),
  });
  if (!response.ok) throw await parseError(response);
  return response.json();
}

export async function refreshSellerToken(refreshToken: string): Promise<{
  accessToken: string;
  refreshToken: string;
}> {
  const response = await fetch(`${SELLER_API_BASE}/auth/refresh`, {
    method: 'POST',
    headers: publicHeaders(),
    body: JSON.stringify({ refreshToken }),
  });
  if (!response.ok) throw await parseError(response);
  return response.json();
}

export async function getSellerMe(accessToken: string): Promise<SellerMe> {
  const response = await fetch(`${SELLER_API_BASE}/me`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  });
  if (!response.ok) throw await parseError(response);
  return response.json();
}

export async function linkSellerTelegram(request: {
  telegramPhone: string;
  telegramUserId?: string;
}): Promise<SellerMe> {
  const response = await makeAuthenticatedRequest(`${SELLER_API_BASE}/me/telegram`, {
    method: 'PATCH',
    body: JSON.stringify(request),
  });
  if (!response.ok) throw await parseError(response);
  return response.json();
}

let linkInFlight: Promise<void> | null = null;

/** Bind Telegram session phone to seller when missing (needed for order attribution). */
export async function ensureSellerTelegramLinked(): Promise<void> {
  if (linkInFlight) return linkInFlight;

  linkInFlight = (async () => {
    const accessToken = getSellerAccessToken();
    if (!accessToken) return;

    const me = await getSellerMe(accessToken);
    if (me.telegramPhone) return;

    const global = getGlobal();
    const currentUser = global.currentUserId
      ? global.users?.byId[global.currentUserId]
      : undefined;
    const phone = currentUser?.phoneNumber?.replace(/\D/g, '');
    if (!phone) {
      throw new SellerApiError(
        'SELLER_PHONE_REQUIRED',
        SELLER_ERROR_RU.SELLER_PHONE_REQUIRED,
      );
    }

    await linkSellerTelegram({
      telegramPhone: phone,
      telegramUserId: currentUser?.id,
    });
  })().finally(() => {
    linkInFlight = null;
  });

  return linkInFlight;
}
